import { getCurrentSession } from './auth.ts';
import { applyTenantRequestHeaders } from './tenantRequestContext';

type AppIdentity = {
  uid: string;
  email?: string | null;
};

type AdminToolsRequestError = Error & {
  code?: string;
  status?: number;
};

export type AdminBillingAccessState = {
  ok: true;
  isAdmin: true;
  canAccessAdminTab?: boolean;
  email: string | null;
  tools: {
    resetTrial?: boolean;
    assetClassAvailabilityRead?: boolean;
    assetClassAvailabilityWrite?: boolean;
  };
};

export type BusinessAdminAccessState = {
  ok: true;
  canAccessBusiness: boolean;
  isPlatformAdmin: boolean;
  isBusinessAdmin: boolean;
  isFoundingPartnerDemo?: boolean;
  businessAccessReason?: string | null;
  tenantSlug: string | null;
  email: string | null;
};

export type BusinessPayoutStatus = 'Current Earnings' | 'Pending Payment' | 'Paid';

export type BusinessPayout = {
  id: string;
  period: string;
  amount: number;
  status: BusinessPayoutStatus;
  paidAt: string | null;
  paymentReference: string | null;
  paymentNotes: string | null;
};

export type BusinessMetricsState = {
  partnerConfigured: boolean;
  activeSubscribers: number;
  totalMRR: number | null;
  netMRR: number | null;
  netMrrDeductions?: { stripeFees: number; refunds: number; chargebacks: number };
  partnerRevenueShare: number | null;
  partnerMonthlyPrice: number | null;
  yourEarnings: number | null;
  currentEarnings: number | null;
  recentSubscribers: number;
  trialConversionRate: number;
  totalTrials: number;
  activeTrials: number;
  revenueGrowthPercent: number | null;
  revenueGrowth: Array<{ name: string; mrr: number | null; netMrr: number | null; partnerRevenue: number | null }>;
  trialsAndConversions: Array<{ name: string; trials: number; conversions: number }>;
  payoutHistory: BusinessPayout[];
  conversionFunnel: Array<{ stage: string; value: number; percent: number }>;
};

export type TenantPayoutLedger = {
  ok: true;
  tenantSlug: string;
  tenantId: string | null;
  currentEarnings: number;
  payouts: BusinessPayout[];
};

export type MarkPayoutAsPaidPayload = {
  payoutId: string;
  paidAt?: string;
  paymentReference?: string;
  paymentNotes?: string;
};

export type MarkPayoutAsPaidResponse = {
  ok: true;
  payout: BusinessPayout;
};

export type PayoutLedgerStatus = 'Current Month' | 'Pending Payment' | 'Paid';

export type PayoutLedgerRow = {
  period: string;
  periodLabel: string;
  grossRevenue: number;
  partnerEarnings: number;
  tradeProofRevenue: number;
  status: PayoutLedgerStatus;
  payoutId: string | null;
  paidOn: string | null;
  reference: string | null;
};

export type PayoutTenantOption = {
  tenantSlug: string;
  tenantName: string;
};

export type PayoutOverviewResponse = {
  ok: true;
  tenants: PayoutTenantOption[];
  tenantSlug: string;
  tenantName: string;
  history: PayoutLedgerRow[];
};


export type BusinessMetricsResponse = {
  ok: true;
  tenantSlug: string;
  partnerConfigured: boolean;
  metrics: BusinessMetricsState;
};

export type AdminAssetClass = 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO';

export type AdminAssetAvailabilityResolvedFrom = 'tenant_persistence' | 'tenant_override' | 'deployment_default' | 'env_fallback';

export type AdminAssetAvailabilityTenantOption = {
  tenantSlug: string | null;
  tenantLabel: string;
};

export type AdminAssetClassAvailabilityState = {
  ok: true;
  source: 'environment' | string;
  resolvedFrom: AdminAssetAvailabilityResolvedFrom;
  selectionMode?: 'request_context' | 'deployment_default' | 'selected_tenant' | string;
  tenantSlug: string | null;
  tenantLabel: string;
  tenantOptions: AdminAssetAvailabilityTenantOption[];
  replayEnabled: boolean;
  enabledAssetClasses: AdminAssetClass[];
  navigationLayoutMode?: 'both' | 'side_only' | 'top_only';
  supportedAssetClasses: AdminAssetClass[];
  assetClasses: Array<{
    assetClass: AdminAssetClass;
    enabled: boolean;
  }>;
  writable: boolean;
  detail?: string;
};

export type SaveAdminAssetClassAvailabilityPayload = {
  tenantSlug: string;
  replayEnabled: boolean;
  enabledAssetClasses: AdminAssetClass[];
  navigationLayoutMode: 'both' | 'side_only' | 'top_only';
};

export type AdminTrialResetResponse = {
  ok: true;
  reset: boolean;
  deleted: boolean;
  rowFound: boolean;
  tenantId: string;
  tenantSlug: string;
  tenantIsActive: boolean;
  email: string;
  profileId: string;
};

export type AdminTrialResetPayload = {
  tenantSlug: 'trade-proof' | 'warrior-trading';
  email: string;
};

export type FoundingPartnerOutreachTouch = {
  id: number | string;
  prospectId?: number | string;
  touchNumber: number;
  type: string;
  displayLabel: string;
  outreachToken: string;
  outreachUrl: string | null;
  emailSentAt: string | null;
  emailSentSource?: 'automated' | 'manual';
  emailSubject?: string | null;
  emailBodyText?: string | null;
  emailOpenedAt: string | null;
  emailOpenedSource?: 'automated' | 'manual';
  demoClickedAt: string | null;
  demoClickedSource?: 'automated' | 'manual';
  loggedInAt: string | null;
  loggedInSource?: 'automated' | 'manual';
  notes: string | null;
  createdAt: string;
  status: 'NOT SENT' | 'SENT' | 'OPENED' | 'DEMO CLICKED' | 'LOGGED IN';
  lifecycleStatus: 'ACTIVE' | 'HOLD' | 'ARCHIVED';
  qualificationStatus: 'HIGH_PRIORITY' | 'QUALIFIED' | 'VERIFY' | 'DISQUALIFIED' | null;
  researchConfidence: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  audienceEstimate: number | null;
  paidMemberEstimate: number | null;
  primaryAssetClass: 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO' | 'MULTI_ASSET' | 'OPTIONS' | null;
  productCompatibility: 'COMPATIBLE' | 'PARTIAL' | 'NOT_COMPATIBLE' | 'VERIFY' | null;
};

export type FoundingPartnerOutreachEvent = {
  eventType: 'demo_link_visited' | 'demo_login_success' | 'email_sent' | 'email_opened';
  source: 'system' | 'manual';
  occurredAt: string;
  touchId?: number | string | null;
};

export type FoundingPartnerOutreachProspect = {
  priorityOrder: number | null;
  websiteUrl: string | null;
  contactMethod: 'EMAIL' | 'CONTACT_FORM' | 'WHOP' | 'DISCORD' | 'LINKEDIN' | 'X_TWITTER' | 'INSTAGRAM' | 'OTHER' | null;
  contactUrl: string | null;
  tenantSlug: string;
  partnerName: string;
  contactName: string;
  demoEmail: string;
  demoLoginEmail?: string | null;
  demoLoginUsername?: string | null;
  loginIdentifier?: string | null;
  loginType?: 'email' | 'username' | null;
  additionalEmails?: string[];
  outreachUrl: string | null;
  outreachToken?: string;
  notes: string | null;
  journalGrade: 'A' | 'B' | 'C' | 'D' | null;
  journalGradeNote: string | null;
  aiNextAction: string | null;
  aiNextActionGeneratedAt: string | null;
  emailSentAt: string | null;
  emailOpenedAt: string | null;
  demoVisitedAt: string | null;
  demoLoginAt: string | null;
  lastActivityAt: string | null;
  status: 'NOT SENT' | 'SENT' | 'OPENED' | 'DEMO CLICKED' | 'LOGGED IN';
  touches: FoundingPartnerOutreachTouch[];
  activity: FoundingPartnerOutreachEvent[];
  emailOpenFirstAt?: string | null;
  emailOpenLastAt?: string | null;
  emailOpenCount?: number;
  ctaClickFirstAt?: string | null;
  ctaClickLastAt?: string | null;
  ctaClickCount?: number;
  partnerAccess?: {
    evaluationId: string;
    firstAccessAt: string | null;
    lastAccessAt: string | null;
    platformVisits: number;
    lastActivityAt: string | null;
    evaluationStartAt: string | null;
    evaluationExpiresAt: string | null;
    daysRemaining: number | null;
    status: 'pending' | 'active' | 'expired' | 'revoked';
    extension_history?: Array<{ previousExpiresAt: string | null; newExpiresAt: string; days: number | null; custom: boolean; reason: string | null; createdAt: string }>;
  } | null;
};

export type FoundingPartnerOutreachResponse = { ok: true; prospects: FoundingPartnerOutreachProspect[] };

type FetchAdminAssetClassAvailabilityOptions = {
  tenantSlug?: string | null;
};

const ADMIN_TOOLS_REQUEST_TIMEOUT_MS = Number(
  import.meta.env?.VITE_ADMIN_TOOLS_TIMEOUT_MS || import.meta.env?.VITE_WORKSPACE_LOAD_TIMEOUT_MS || 8000,
);
const FOUNDING_PARTNER_HUBSPOT_SYNC_TIMEOUT_MS = Number(
  import.meta.env?.VITE_FOUNDING_PARTNER_HUBSPOT_SYNC_TIMEOUT_MS || 30000,
);
const FOUNDING_PARTNER_DEMO_VERIFY_TIMEOUT_MS = Number(
  import.meta.env?.VITE_FOUNDING_PARTNER_DEMO_VERIFY_TIMEOUT_MS || 60000,
);
const FOUNDING_PARTNER_PROPOSAL_TIMEOUT_MS = Number(import.meta.env?.VITE_FOUNDING_PARTNER_PROPOSAL_TIMEOUT_MS || 120000);

const tpStripeFlashLog = (event: string, payload: Record<string, unknown> = {}) => {
  try { console.info('[TP-STRIPE-FLASH]', event, { ts: Date.now(), ...payload }); } catch {}
};

function buildRequestError(message: string, status?: number, code?: string) {
  const error = new Error(message) as AdminToolsRequestError;
  if (status != null) {
    error.status = status;
  }
  if (code) {
    error.code = code;
  }
  return error;
}

async function readJson<T>(res: Response): Promise<T> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  const payload = body && typeof body === 'object' ? body as Record<string, unknown> : null;

  if (!res.ok) {
    const fallbackMessage = res.status === 404
      ? `Admin request endpoint unavailable (HTTP ${res.status}). The deployed backend may need redeployment.`
      : `Admin request failed (HTTP ${res.status}).`;
    throw buildRequestError(
      String(payload?.detail || payload?.error || fallbackMessage).trim() || fallbackMessage,
      res.status,
      String(payload?.error || '').trim().toLowerCase() || undefined,
    );
  }

  return body as T;
}

function buildTimeoutError(label: string, timeoutMs: number) {
  return buildRequestError(`${label} request timed out after ${timeoutMs}ms.`);
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit, timeoutMs: number, label: string): Promise<Response> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  try {
    const request = fetch(input, controller ? { ...init, signal: controller.signal } : init);
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        try {
          controller?.abort();
        } catch {}
        reject(buildTimeoutError(label, timeoutMs));
      }, timeoutMs);
    });

    return await Promise.race([request, timeout]);
  } catch (error: unknown) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw buildTimeoutError(label, timeoutMs);
    }
    throw error;
  } finally {
    if (timeoutId != null) {
      clearTimeout(timeoutId);
    }
  }
}

async function buildAdminAuthHeaders(identity: AppIdentity | null) {
  if (!identity?.uid) {
    throw buildRequestError('missing_user_id');
  }

  const session = await getCurrentSession();
  const accessToken = String(session?.access_token || '').trim();
  if (!accessToken) {
    throw buildRequestError('missing_access_token');
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
  };

  return applyTenantRequestHeaders(headers);
}

function buildAdminAssetAvailabilityPath(options?: FetchAdminAssetClassAvailabilityOptions) {
  const params = new URLSearchParams();
  if (options && Object.prototype.hasOwnProperty.call(options, 'tenantSlug')) {
    const tenantSlug = String(options?.tenantSlug || '').trim();
    params.set('tenantSlug', tenantSlug || 'deployment-default');
  }

  const query = params.toString();
  return query ? `/api/admin/billing/asset-class-availability?${query}` : '/api/admin/billing/asset-class-availability';
}

export async function fetchAdminBillingAccess(identity: AppIdentity | null, options?: { correlationId?: string }): Promise<AdminBillingAccessState> {
  const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const correlationId = options?.correlationId || null;
  tpStripeFlashLog('client_admin_access_start', { correlationId });
  try {
  const res = await fetchWithTimeout(
    '/api/admin/billing/access',
    {
      headers: {
        ...(await buildAdminAuthHeaders(identity)),
        ...(correlationId ? { 'x-tp-bootstrap-id': correlationId } : {}),
      },
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Admin tools access',
  );

    const result = await readJson<AdminBillingAccessState>(res);
    tpStripeFlashLog('client_admin_access_finish', { correlationId, durationMs: Number(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt).toFixed(1)), success: true });
    return result;
  } catch (error) {
    tpStripeFlashLog('client_admin_access_finish', { correlationId, durationMs: Number(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt).toFixed(1)), success: false });
    throw error;
  }
}

export async function fetchFoundingPartnerOutreach(identity: AppIdentity | null): Promise<FoundingPartnerOutreachResponse> {
  const res = await fetchWithTimeout('/api/outreach', { headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Founding partner outreach');
  return readJson<FoundingPartnerOutreachResponse>(res);
}

export async function extendWarriorPartnerAccess(identity: AppIdentity | null, evaluationId: string, payload: { days?: number; expiresAt?: string; reason?: string }) {
  const res = await fetchWithTimeout(`/api/outreach/warrior-access/${encodeURIComponent(evaluationId)}/extend`, { method: 'POST', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Extend Warrior partner access');
  return readJson<{ ok: true; evaluation: { expires_at: string; status: string } }>(res);
}

export type BetaTester = {
  id: string;
  name: string;
  email: string;
  notes: string;
  personalMessage: string;
  status: 'active' | 'archived';
  invitationStatus: 'not_sent' | 'sending' | 'sent' | 'failed' | 'redeemed' | 'revoked';
  sendError?: string | null;
  sentAt: string | null;
  providerMessageId: string | null;
  templateId: string | null;
  templateName: string | null;
  expiresAt: string | null;
  redeemedAt: string | null;
  lastEmailSentAt?: string | null;
  partnershipSignedUpAt?: string | null;
};

export type AdminEmailTemplateSummary = {
  id: string;
  name: string;
  subject: string;
  defaultPersonalMessage: string;
  systemKey: string | null;
  evaluationType: 'WARRIOR_TRADING_EVALUATION' | 'GENERIC_PARTNERSHIP_EVALUATION' | null;
  createdAt: string | null;
  updatedAt: string | null;
  versionHash: string;
};

export type AdminEmailTemplate = AdminEmailTemplateSummary & {
  htmlContent: string;
  textContent: string;
};

export type AdminEmailTemplateInput = {
  name: string;
  subject: string;
  htmlContent: string;
  textContent: string;
  defaultPersonalMessage: string;
  evaluationType?: 'WARRIOR_TRADING_EVALUATION' | 'GENERIC_PARTNERSHIP_EVALUATION' | null;
};

export type AdminEmailPreview = { subject: string; text: string; html: string; recipient?: string };

export const BETA_INVITATION_DEFAULT_MESSAGE = 'I’m inviting you to try Trade-Proof and see what your trading history can teach you.\n\nI’d love to hear what you think as we continue improving the platform.';

export async function fetchBetaTesters(identity: AppIdentity | null) {
  const res = await fetchWithTimeout('/api/admin/beta/testers', { headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Beta testers');
  return readJson<{ ok: true; testers: BetaTester[] }>(res);
}

export async function deleteBetaTesters(identity: AppIdentity | null, ids: string[]) {
  const res = await fetchWithTimeout('/api/admin/beta/testers', {
    method: 'DELETE',
    headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Delete beta testers');
  return readJson<{ ok: true; deletedIds: string[] }>(res);
}

export async function fetchAdminEmailTemplates(identity: AppIdentity | null) {
  const res = await fetchWithTimeout('/api/admin/beta/templates', { headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Email templates');
  return readJson<{ ok: true; templates: AdminEmailTemplateSummary[] }>(res);
}

export async function fetchAdminEmailTemplate(identity: AppIdentity | null, id: string) {
  const res = await fetchWithTimeout(`/api/admin/beta/templates/${encodeURIComponent(id)}`, { headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Email template');
  return readJson<{ ok: true; template: AdminEmailTemplate }>(res);
}

export async function createAdminEmailTemplate(identity: AppIdentity | null, payload: AdminEmailTemplateInput) {
  const res = await fetchWithTimeout('/api/admin/beta/templates', { method: 'POST', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Create email template');
  return readJson<{ ok: true; template: AdminEmailTemplate }>(res);
}

export async function updateAdminEmailTemplate(identity: AppIdentity | null, id: string, payload: AdminEmailTemplateInput, expectedVersionHash: string) {
  const res = await fetchWithTimeout(`/api/admin/beta/templates/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify({ ...payload, expectedVersionHash }) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Save email template');
  return readJson<{ ok: true; template: AdminEmailTemplate }>(res);
}

export async function duplicateAdminEmailTemplate(identity: AppIdentity | null, id: string, name?: string) {
  const res = await fetchWithTimeout(`/api/admin/beta/templates/${encodeURIComponent(id)}/duplicate`, { method: 'POST', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Duplicate email template');
  return readJson<{ ok: true; template: AdminEmailTemplate }>(res);
}

export async function deleteAdminEmailTemplate(identity: AppIdentity | null, id: string) {
  const res = await fetchWithTimeout(`/api/admin/beta/templates/${encodeURIComponent(id)}`, { method: 'DELETE', headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Delete email template');
  return readJson<{ ok: true; deleted: boolean }>(res);
}

export async function previewAdminEmailTemplate(identity: AppIdentity | null, payload: { template: AdminEmailTemplateInput; name?: string; personalMessage?: string }) {
  const res = await fetchWithTimeout('/api/admin/beta/templates/preview', { method: 'POST', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Preview email template');
  return readJson<{ ok: true; preview: AdminEmailPreview }>(res);
}

export async function createBetaTester(identity: AppIdentity | null, payload: { name: string; email: string; notes?: string }) {
  const res = await fetchWithTimeout('/api/admin/beta/testers', { method: 'POST', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Add beta tester');
  return readJson<{ ok: true; tester: BetaTester }>(res);
}

export async function updateBetaTester(identity: AppIdentity | null, id: string, payload: { name?: string; email?: string; notes?: string; status?: 'active' | 'archived'; personalMessage?: string }) {
  const res = await fetchWithTimeout(`/api/admin/beta/testers/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Update beta tester');
  return readJson<{ ok: true; tester: BetaTester }>(res);
}

export async function previewBetaInvitation(identity: AppIdentity | null, testerId: string, templateId: string, personalMessage: string) {
  const res = await fetchWithTimeout('/api/admin/beta/preview', { method: 'POST', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify({ testerId, templateId, personalMessage }) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Preview beta invitation');
  return readJson<{ ok: true; subject: string; text: string; html: string; recipient: string }>(res);
}

export async function sendBetaInvitation(identity: AppIdentity | null, id: string, templateId: string, personalMessage: string, resend = false) {
  const res = await fetchWithTimeout(`/api/admin/beta/testers/${encodeURIComponent(id)}/send`, { method: 'POST', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify({ templateId, personalMessage, resend }) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, resend ? 'Resend beta invitation' : 'Send beta invitation');
  return readJson<{ ok: true; tester: BetaTester }>(res);
}

export async function revokeBetaTester(identity: AppIdentity | null, id: string) {
  const res = await fetchWithTimeout(`/api/admin/beta/testers/${encodeURIComponent(id)}/revoke`, { method: 'POST', headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Revoke beta access');
  return readJson<{ ok: true; revoked: boolean }>(res);
}

export type EmailPortalHistoryResponse = {
  ok: true;
  previouslySent: boolean;
  matchingTemplate: {
    sentAt: string;
    templateName: string;
    providerMessageId: string | null;
  } | null;
  lastSent: {
    sentAt: string;
    templateName: string;
    providerMessageId: string | null;
  } | null;
  totalCount: number;
};

export type EmailPortalSendPayload = {
  templateId: string;
  recipientEmail: string;
  recipientName?: string;
  betaTesterId?: string;
  subject?: string;
  personalMessage?: string;
  idempotencyKey?: string;
};

export type EmailPortalSendResponse = {
  ok: true;
  logId: string;
  recipientEmail: string;
  recipientName: string | null;
  templateName: string;
  subject: string;
  sentAt: string;
  providerMessageId: string | null;
  isDuplicate?: boolean;
};

export type EmailPortalPreviewPayload = {
  templateId: string;
  recipientEmail?: string;
  recipientName?: string;
  subject?: string;
  personalMessage?: string;
};

export async function fetchEmailPortalHistory(
  identity: AppIdentity | null,
  email: string,
  templateId?: string,
): Promise<EmailPortalHistoryResponse> {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (templateId) params.set('templateId', templateId);
  const res = await fetchWithTimeout(
    `/api/admin/email-portal/history?${params.toString()}`,
    { headers: await buildAdminAuthHeaders(identity) },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Email send history',
  );
  return readJson<EmailPortalHistoryResponse>(res);
}

export async function previewEmailPortalEmail(
  identity: AppIdentity | null,
  payload: EmailPortalPreviewPayload,
): Promise<{ ok: true; preview: AdminEmailPreview }> {
  const res = await fetchWithTimeout(
    '/api/admin/email-portal/preview',
    {
      method: 'POST',
      headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Preview email portal email',
  );
  return readJson<{ ok: true; preview: AdminEmailPreview }>(res);
}

export async function sendEmailPortalEmail(
  identity: AppIdentity | null,
  payload: EmailPortalSendPayload,
): Promise<EmailPortalSendResponse> {
  const res = await fetchWithTimeout(
    '/api/admin/email-portal/send',
    {
      method: 'POST',
      headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Send email portal email',
  );
  return readJson<EmailPortalSendResponse>(res);
}


export async function createFoundingPartnerOutreach(identity: AppIdentity | null, payload: { tenantSlug: string; partnerName: string; contactName: string; demoEmail: string; websiteUrl?: string }): Promise<{ ok: true; prospect: FoundingPartnerOutreachProspect }> {
  const res = await fetchWithTimeout('/api/outreach/prospects', { method: 'POST', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Add founding partner');
  return readJson<{ ok: true; prospect: FoundingPartnerOutreachProspect }>(res);
}

export async function syncCanonicalFoundingPartnerProspects(identity: AppIdentity | null): Promise<{ ok: true; added: number; updated: number; unchanged: number; processed: number }> {
  const res = await fetchWithTimeout('/api/outreach/prospects/sync', { method: 'POST', headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Sync founding partner prospects');
  return readJson<{ ok: true; added: number; updated: number; unchanged: number; processed: number }>(res);
}

export async function fetchFoundingPartnerDemoAccess(identity: AppIdentity | null, tenantSlug: string): Promise<{ ok: true; tenantSlug: string; loginUsername: string; loginIdentifier?: string; loginType?: 'email' | 'username'; loginConfigured: boolean; membershipReady: boolean; accessReady: boolean; demoPassword: string }> {
  const res = await fetchWithTimeout(`/api/outreach/${encodeURIComponent(tenantSlug)}/demo-access`, { headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Founding partner demo access');
  return readJson<{ ok: true; tenantSlug: string; loginUsername: string; loginIdentifier?: string; loginType?: 'email' | 'username'; loginConfigured: boolean; membershipReady: boolean; accessReady: boolean; demoPassword: string }>(res);
}

export async function verifyFoundingPartnerDemoAccess(identity: AppIdentity | null): Promise<{ ok: true; processed: number; ready: number; issues: Array<{ tenantSlug: string; error?: string }> }> {
  const res = await fetchWithTimeout('/api/outreach/demo-access/verify', { method: 'POST', headers: await buildAdminAuthHeaders(identity) }, FOUNDING_PARTNER_DEMO_VERIFY_TIMEOUT_MS, 'Verify founding partner demo access');
  return readJson<{ ok: true; processed: number; ready: number; issues: Array<{ tenantSlug: string; error?: string }> }>(res);
}

export type FoundingPartnerProposalPreview = { tenantSlug: string; partnerName: string; contactName: string; recipient: string | null; sender: { from: string | null; replyTo: string | null }; subject: string | null; outreachFitSentence: string | null; partnerRevenueShare: number | null; outreachUrl: string | null; attachmentFilename: string; attachmentStatus?: 'pending' | 'ready' | 'failed' | 'not_required'; stage?: string | null; manualSendOverride?: boolean; emailPreview: { subject: string; text: string; html: string } | null; ready: boolean; reason: string | null; diagnosticCode?: string | null; proposalTouch?: { displayLabel: string; touchNumber: number; draft: string | null } | null };
export async function previewFoundingPartnerProposals(identity: AppIdentity | null, tenantSlugs: string[], manualSendOverrideSlugs: string[] = []) {
  const res = await fetchWithTimeout('/api/outreach/prospects/proposals/preview', { method: 'POST', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify({ tenantSlugs, manualSendOverrideSlugs }) }, FOUNDING_PARTNER_PROPOSAL_TIMEOUT_MS, 'Preview founding partner proposals');
  return readJson<{ ok: true; items: FoundingPartnerProposalPreview[] }>(res);
}
export async function sendFoundingPartnerProposals(identity: AppIdentity | null, tenantSlugs: string[], overrides: Array<{ tenantSlug: string; subjectOverride?: string; bodyTextOverride?: string }> = [], drafts: Array<{ tenantSlug: string; draft: string }> = [], bccMe = false, manualSendOverrideSlugs: string[] = []) {
  const res = await fetchWithTimeout('/api/outreach/prospects/proposals/send', { method: 'POST', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify({ tenantSlugs, overrides, drafts, bccMe, manualSendOverrideSlugs }) }, FOUNDING_PARTNER_PROPOSAL_TIMEOUT_MS, 'Send founding partner proposals');
  return readJson<{ ok: true; results: Array<{ tenantSlug: string; status: 'SENT' | 'DRY_RUN' | 'FAILED' | 'INELIGIBLE' | 'ALREADY_SENT' | 'SEND_IN_PROGRESS' | string; reason?: string }> }>(res);
}

export async function setupFoundingPartnerLogin(identity: AppIdentity | null, tenantSlug: string, payload: { username: string; password: string }): Promise<{ ok: true; tenantSlug: string; partnerName: string; username: string; email: string }> {
  const res = await fetchWithTimeout(`/api/outreach/${encodeURIComponent(tenantSlug)}/login`, { method: 'POST', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Set up partner login');
  return readJson<{ ok: true; tenantSlug: string; partnerName: string; username: string; email: string }>(res);
}

export type FoundingPartnerOutreachSyncResponse = { ok: true; httpStatus?: number; updated: Array<{ tenantSlug: string; sent: boolean; opened: boolean }>; processed: number; sentRecorded: number; openedRecorded: number; skipped: number; errors: string[] };

export async function syncFoundingPartnerOutreach(identity: AppIdentity | null): Promise<FoundingPartnerOutreachSyncResponse> {
  const res = await fetchWithTimeout('/api/outreach/hubspot-sync', { method: 'POST', headers: await buildAdminAuthHeaders(identity) }, FOUNDING_PARTNER_HUBSPOT_SYNC_TIMEOUT_MS, 'HubSpot outreach sync');
  return readJson<FoundingPartnerOutreachSyncResponse>(res);
}

export async function simulateFoundingPartnerTestStage(identity: AppIdentity | null, stage: 'original' | 'campaign_follow_up_1' | 'campaign_follow_up_2' | 'campaign_follow_up_3', outreachUrl?: string) {
  const res = await fetchWithTimeout('/api/outreach/prospects/test-qa/simulate', { method: 'POST', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify({ stage, outreachUrl }) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Simulate TEST outreach stage');
  return readJson<{ ok: true; prospect: FoundingPartnerOutreachProspect }>(res);
}

export async function resetFoundingPartnerTestQa(identity: AppIdentity | null) {
  const res = await fetchWithTimeout('/api/outreach/prospects/test-qa/reset', { method: 'POST', headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Reset TEST outreach QA');
  return readJson<{ ok: true; prospect: FoundingPartnerOutreachProspect }>(res);
}

export async function createFoundingPartnerOutreachTouch(identity: AppIdentity | null, tenantSlug: string, payload?: { displayLabel?: string; notes?: string }): Promise<{ ok: true; prospect: FoundingPartnerOutreachProspect }> {
  const res = await fetchWithTimeout(`/api/outreach/${encodeURIComponent(tenantSlug)}/touches`, { method: 'POST', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify(payload || {}) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Create outreach follow-up');
  return readJson<{ ok: true; prospect: FoundingPartnerOutreachProspect }>(res);
}

export async function updateFoundingPartnerTouchStatus(identity: AppIdentity | null, tenantSlug: string, touchIdentifier: number | string, payload: { emailSentAt?: string | null; emailOpenedAt?: string | null; demoClickedAt?: string | null; loggedInAt?: string | null; notes?: string | null }): Promise<{ ok: true; prospect: FoundingPartnerOutreachProspect }> {
  const res = await fetchWithTimeout(`/api/outreach/${encodeURIComponent(tenantSlug)}/touches/${encodeURIComponent(String(touchIdentifier))}`, { method: 'PATCH', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Update touch status');
  return readJson<{ ok: true; prospect: FoundingPartnerOutreachProspect }>(res);
}

export async function updateFoundingPartnerOutreachStatus(identity: AppIdentity | null, tenantSlug: string, payload: { emailSentAt: string | null; emailOpenedAt: string | null; notes: string | null; journalGrade?: 'A' | 'B' | 'C' | 'D' | null; journalGradeNote?: string | null; contactMethod?: FoundingPartnerOutreachProspect['contactMethod']; contactUrl?: string | null }): Promise<{ ok: true; prospect: FoundingPartnerOutreachProspect }> {
  const res = await fetchWithTimeout(`/api/outreach/${encodeURIComponent(tenantSlug)}/status`, { method: 'PATCH', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Update outreach status');
  return readJson<{ ok: true; prospect: FoundingPartnerOutreachProspect }>(res);
}

export async function updateFoundingPartnerLifecycle(identity: AppIdentity | null, tenantSlug: string, lifecycleStatus: 'ACTIVE' | 'HOLD' | 'ARCHIVED'): Promise<{ ok: true; prospect: FoundingPartnerOutreachProspect }> {
  const res = await fetchWithTimeout(`/api/outreach/${encodeURIComponent(tenantSlug)}/lifecycle`, { method: 'PATCH', headers: { ...(await buildAdminAuthHeaders(identity)), 'Content-Type': 'application/json' }, body: JSON.stringify({ lifecycleStatus }) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Update prospect lifecycle');
  return readJson<{ ok: true; prospect: FoundingPartnerOutreachProspect }>(res);
}

export async function generateFoundingPartnerOutreachRecommendations(identity: AppIdentity | null): Promise<{ ok: true; generated: Array<{ tenantSlug: string; recommendation: string | null }> }> {
  const res = await fetchWithTimeout('/api/outreach/ai-next-actions/generate', { method: 'POST', headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Generate outreach recommendations');
  return readJson<{ ok: true; generated: Array<{ tenantSlug: string; recommendation: string | null }> }>(res);
}

export async function regenerateFoundingPartnerOutreachRecommendation(identity: AppIdentity | null, tenantSlug: string): Promise<{ ok: true; tenantSlug: string; recommendation: string }> {
  const res = await fetchWithTimeout(`/api/outreach/${encodeURIComponent(tenantSlug)}/ai-next-action`, { method: 'POST', headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Regenerate outreach recommendation');
  return readJson<{ ok: true; tenantSlug: string; recommendation: string }>(res);
}

export async function resetFoundingPartnerOutreach(identity: AppIdentity | null, tenantSlug: string): Promise<{ ok: true; reset: true }> {
  const res = await fetchWithTimeout(`/api/outreach/${encodeURIComponent(tenantSlug)}/reset`, { method: 'POST', headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Reset outreach tracking');
  return readJson<{ ok: true; reset: true }>(res);
}

export async function resetFoundingPartnerOutreachTouch(identity: AppIdentity | null, tenantSlug: string, touchId: number | string): Promise<{ ok: true; prospect: FoundingPartnerOutreachProspect }> {
  const res = await fetchWithTimeout(`/api/outreach/${encodeURIComponent(tenantSlug)}/touches/${encodeURIComponent(String(touchId))}/reset`, { method: 'POST', headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Reset outreach touch');
  return readJson<{ ok: true; prospect: FoundingPartnerOutreachProspect }>(res);
}

export async function removeFoundingPartnerOutreachTouch(identity: AppIdentity | null, tenantSlug: string, touchId: number | string): Promise<{ ok: true; prospect: FoundingPartnerOutreachProspect }> {
  const res = await fetchWithTimeout(`/api/outreach/${encodeURIComponent(tenantSlug)}/touches/${encodeURIComponent(String(touchId))}`, { method: 'DELETE', headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Remove outreach touch');
  return readJson<{ ok: true; prospect: FoundingPartnerOutreachProspect }>(res);
}

export async function removeFoundingPartnerOutreachProspect(identity: AppIdentity | null, tenantSlug: string): Promise<{ ok: true; removed: true }> {
  const res = await fetchWithTimeout(`/api/outreach/${encodeURIComponent(tenantSlug)}`, { method: 'DELETE', headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Remove outreach prospect');
  return readJson<{ ok: true; removed: true }>(res);
}

export async function resetAllFoundingPartnerOutreach(identity: AppIdentity | null): Promise<{ ok: true; reset: true }> {
  const res = await fetchWithTimeout('/api/outreach/reset-all', { method: 'POST', headers: await buildAdminAuthHeaders(identity) }, ADMIN_TOOLS_REQUEST_TIMEOUT_MS, 'Reset all outreach tracking');
  return readJson<{ ok: true; reset: true }>(res);
}

/**
 * Server-authoritative access check for the Business (Partner Portal) page. A user
 * may access Business when they are a platform admin OR an active business_admin of
 * the current tenant. The server resolves the tenant from the authorized session's
 * x-tenant-slug, so direct /business URL access is enforced server-side too.
 */
export async function fetchBusinessAdminAccess(identity: AppIdentity | null): Promise<BusinessAdminAccessState> {
  const res = await fetchWithTimeout(
    '/api/admin/business/access',
    {
      headers: await buildAdminAuthHeaders(identity),
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Business access',
  );

  return readJson<BusinessAdminAccessState>(res);
}

/**
 * Business (Partner Portal) metrics for the CURRENT tenant. Server-computed from
 * real subscriber billing data — never client demo values. Requires platform
 * admin or active business_admin access.
 */
export async function fetchBusinessMetrics(identity: AppIdentity | null): Promise<BusinessMetricsResponse> {
  const res = await fetchWithTimeout(
    '/api/admin/business/metrics',
    {
      headers: await buildAdminAuthHeaders(identity),
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Business metrics',
  );

  return readJson<BusinessMetricsResponse>(res);
}

/**
 * Read the payout ledger for a tenant (source of truth for payout history).
 * Business-admin scoped to their own tenant; platform admin may pass tenantSlug.
 */
export async function fetchTenantPayoutLedger(
  identity: AppIdentity | null,
  tenantSlug?: string,
): Promise<TenantPayoutLedger> {
  const params = new URLSearchParams();
  if (tenantSlug) params.set('tenantSlug', tenantSlug);
  const query = params.toString();
  const res = await fetchWithTimeout(
    `/api/admin/business/payouts${query ? `?${query}` : ''}`,
    {
      headers: await buildAdminAuthHeaders(identity),
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Tenant payouts',
  );
  return readJson<TenantPayoutLedger>(res);
}

/** Record an actual payment against a frozen payout (platform admin only). */
export async function markPayoutAsPaid(
  identity: AppIdentity | null,
  payload: MarkPayoutAsPaidPayload,
): Promise<MarkPayoutAsPaidResponse> {
  const res = await fetchWithTimeout(
    '/api/admin/business/payouts/mark-paid',
    {
      method: 'POST',
      headers: {
        ...(await buildAdminAuthHeaders(identity)),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Mark payout paid',
  );
  return readJson<MarkPayoutAsPaidResponse>(res);
}

/**
 * Payout ledger for one partner (platform admin only).
 * Passing tenantSlug reloads the ledger for that partner.
 */
export async function fetchPayoutOverview(
  identity: AppIdentity | null,
  tenantSlug?: string,
): Promise<PayoutOverviewResponse> {
  const params = new URLSearchParams();
  if (tenantSlug) params.set('tenantSlug', tenantSlug);
  const query = params.toString();
  const res = await fetchWithTimeout(
    `/api/admin/business/payout-overview${query ? `?${query}` : ''}`,
    {
      headers: await buildAdminAuthHeaders(identity),
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Payout overview',
  );
  return readJson<PayoutOverviewResponse>(res);
}

export async function resetAdminTrial(identity: AppIdentity | null, payload: AdminTrialResetPayload): Promise<AdminTrialResetResponse> {
  const res = await fetchWithTimeout(
    '/api/admin/billing/reset-trial',
    {
      method: 'POST',
      headers: {
        ...(await buildAdminAuthHeaders(identity)),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Admin trial reset',
  );

  return readJson<AdminTrialResetResponse>(res);
}

export type AdminSupportTicketStatus = 'OPEN' | 'IN_REVIEW' | 'RESOLVED' | 'CLOSED';

export type AdminSupportTicketListItem = {
  id: string;
  status: AdminSupportTicketStatus;
  summary: string;
  user_email: string | null;
  tenant_slug: string;
  escalation_reason: string;
  created_at: string;
};

export type AdminSupportTicketTranscriptMessage = {
  role: 'user' | 'assistant';
  content: string;
};

export type AdminSupportTicketDetail = AdminSupportTicketListItem & {
  user_id: string;
  transcript: AdminSupportTicketTranscriptMessage[];
  updated_at: string;
};

/** GET /api/admin/support-tickets?status=... — list tickets (optional status filter). */
export async function fetchAdminSupportTickets(
  identity: AppIdentity | null,
  status?: AdminSupportTicketStatus | '',
): Promise<{ ok: true; tickets: AdminSupportTicketListItem[] }> {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  const query = params.toString();
  const res = await fetchWithTimeout(
    `/api/admin/support-tickets${query ? `?${query}` : ''}`,
    { headers: await buildAdminAuthHeaders(identity) },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Support tickets',
  );
  return readJson<{ ok: true; tickets: AdminSupportTicketListItem[] }>(res);
}

/** GET /api/admin/support-tickets/:id — full ticket detail including transcript. */
export async function fetchAdminSupportTicket(
  identity: AppIdentity | null,
  id: string,
): Promise<{ ok: true; ticket: AdminSupportTicketDetail }> {
  const res = await fetchWithTimeout(
    `/api/admin/support-tickets/${encodeURIComponent(id)}`,
    { headers: await buildAdminAuthHeaders(identity) },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Support ticket detail',
  );
  return readJson<{ ok: true; ticket: AdminSupportTicketDetail }>(res);
}

/** PATCH /api/admin/support-tickets/:id — update ticket status. */
export async function updateAdminSupportTicketStatus(
  identity: AppIdentity | null,
  id: string,
  status: AdminSupportTicketStatus,
): Promise<{ ok: true; ticket: AdminSupportTicketDetail }> {
  const res = await fetchWithTimeout(
    `/api/admin/support-tickets/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: {
        ...(await buildAdminAuthHeaders(identity)),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Update support ticket',
  );
  return readJson<{ ok: true; ticket: AdminSupportTicketDetail }>(res);
}

/** DELETE /api/admin/support-tickets/:id — permanently removes a closed ticket. */
export async function deleteAdminSupportTicket(
  identity: AppIdentity | null,
  id: string,
): Promise<{ ok: true; ticketId: string }> {
  const res = await fetchWithTimeout(
    `/api/admin/support-tickets/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
      headers: await buildAdminAuthHeaders(identity),
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Delete support ticket',
  );
  return readJson<{ ok: true; ticketId: string }>(res);
}

export type AdminUserSearchResult = {
  id: string;
  email: string;
};

export type AdminOnboardingActionResponse = {
  ok: true;
  email: string;
  tenantSlug: string;
  launched?: boolean;
  reset?: boolean;
};

/**
 * Email autocomplete for the Admin Tools Onboarding Tour picker. Returns
 * lightweight profile matches (id + email) — internal ids are never displayed.
 */
export async function searchAdminUsers(
  identity: AppIdentity | null,
  search: string,
): Promise<{ ok: true; users: AdminUserSearchResult[] }> {
  const params = new URLSearchParams();
  const normalizedSearch = String(search || '').trim();
  if (normalizedSearch) params.set('search', normalizedSearch);
  const query = params.toString();

  const res = await fetchWithTimeout(
    `/api/admin/onboarding/users${query ? `?${query}` : ''}`,
    {
      headers: await buildAdminAuthHeaders(identity),
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Admin onboarding user search',
  );

  return readJson(res);
}

/** Launch the onboarding walkthrough for ONE user (current admin session tenant). */
export async function launchAdminOnboardingTour(
  identity: AppIdentity | null,
  email: string,
): Promise<AdminOnboardingActionResponse> {
  const res = await fetchWithTimeout(
    '/api/admin/onboarding/launch',
    {
      method: 'POST',
      headers: {
        ...(await buildAdminAuthHeaders(identity)),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Admin onboarding launch',
  );

  return readJson<AdminOnboardingActionResponse>(res);
}

/** Reset onboarding progress for ONE user only (current admin session tenant). */
export async function resetAdminOnboardingTour(
  identity: AppIdentity | null,
  email: string,
  options?: { allTenants?: boolean },
): Promise<AdminOnboardingActionResponse> {
  const res = await fetchWithTimeout(
    '/api/admin/onboarding/reset',
    {
      method: 'POST',
      headers: {
        ...(await buildAdminAuthHeaders(identity)),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, allTenants: options?.allTenants === true }),
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Admin onboarding reset',
  );

  return readJson<AdminOnboardingActionResponse>(res);
}

export type OnboardingTourStateResponse = {
  ok: true;
  launched: boolean;
  reset: boolean;
};

/**
 * Consume-once read of the CURRENT user's server-side onboarding tour state
 * (admin-initiated launch/reset for this user on this tenant). No-op when the
 * browser has no authenticated session.
 */
export async function fetchOnboardingTourState(): Promise<OnboardingTourStateResponse> {
  const session = await getCurrentSession();
  const accessToken = String(session?.access_token || '').trim();
  if (!accessToken) {
    return { ok: true, launched: false, reset: false };
  }

  const headers = applyTenantRequestHeaders({ Authorization: `Bearer ${accessToken}` });
  const res = await fetchWithTimeout(
    '/api/onboarding/tour-state',
    { headers },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Onboarding tour state',
  );

  return readJson<OnboardingTourStateResponse>(res);
}

export async function fetchAdminAssetClassAvailability(
  identity: AppIdentity | null,
  options?: FetchAdminAssetClassAvailabilityOptions,
): Promise<AdminAssetClassAvailabilityState> {
  const res = await fetchWithTimeout(
    buildAdminAssetAvailabilityPath(options),
    {
      headers: await buildAdminAuthHeaders(identity),
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Admin asset availability',
  );

  return readJson<AdminAssetClassAvailabilityState>(res);
}

export async function saveAdminAssetClassAvailability(
  identity: AppIdentity | null,
  payload: SaveAdminAssetClassAvailabilityPayload,
): Promise<AdminAssetClassAvailabilityState> {
  const res = await fetchWithTimeout(
    '/api/admin/billing/asset-class-availability',
    {
      method: 'PATCH',
      headers: {
        ...(await buildAdminAuthHeaders(identity)),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
    ADMIN_TOOLS_REQUEST_TIMEOUT_MS,
    'Admin asset availability save',
  );

  return readJson<AdminAssetClassAvailabilityState>(res);
}
